import { displayName, lifespan, type Person } from '@lineage/core';
import { createContext, memo, useContext, useState } from 'react';
import { usePhotoSrc } from '../../lib/photoSrc';
import s from './PersonCard.module.css';
import { useLang, useT } from '../../i18n';

/** Ready-made portraits by person id, for the sample family (real trees load photos from the API). */
export const SamplePhotos = createContext<ReadonlyMap<string, string> | null>(null);

export const toneOf = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 6;
};

export const initials = (p: Pick<Person, 'firstName' | 'lastName'>) =>
  ((p.firstName?.[0] ?? '') + (p.lastName?.[0] ?? '')).toUpperCase() || '·';

export function Portrait({ person, size = '256', className = '', override, small = false, px }: { person: Person; size?: 'full' | '256'; className?: string; override?: string | null; small?: boolean; px?: number }) {
  const sample = useContext(SamplePhotos);
  const src = usePhotoSrc(person.photoId, size, override ?? sample?.get(person.id));
  const [broken, setBroken] = useState<string | null>(null);
  return (
    <div
      className={`${s.portrait} ${small ? s.mini : ''} ${person.living ? '' : s.gone} ${className}`}
      style={{ background: `var(--p${toneOf(person.id)})`, ...(px ? { width: px, height: px, fontSize: Math.round(px * 0.36) } : null) }}
      aria-hidden="true"
    >
      {src && broken !== src ? <img src={src} alt="" draggable={false} onError={() => setBroken(src)} /> : initials(person)}
    </div>
  );
}

interface Props {
  person: Person;
  relation: string;
  /** What to call them, in the relationship words (PRD 7.2.4); '' when by name. */
  call?: string;
  /** Language of the relationship words, so screen readers and capitals (TÍO) follow it. */
  lang?: string;
  /** Their direction: Arabic words read right to left inside the left-to-right card. */
  dir?: 'ltr' | 'rtl';
  isMe: boolean;
  selected: boolean;
  x: number;
  y: number;
  onSelect: (id: string) => void;
}

export const PersonCard = memo(function PersonCard({ person, relation, call, lang, dir, isMe, selected, x, y, onSelect }: Props) {
  const screenLang = useLang();
  const t = useT().tree;
  const name = displayName(person);
  return (
    <button
      type="button"
      data-person={person.id}
      className={`${s.card} ${selected ? s.selected : ''}`}
      style={{ left: x - 84, top: y }}
      aria-label={[relation ? `${name}, ${relation}` : name, call ? t.callAs(call) : ''].filter(Boolean).join('. ')}
      aria-pressed={selected}
      onClick={() => onSelect(person.id)}
    >
      <Portrait person={person} />
      <span className={s.name}>{name}</span>
      <span className={s.years}>{lifespan(person, screenLang)}</span>
      {relation && <span className={`${s.rel} ${isMe ? s.you : ''}`} lang={lang} dir={dir}>{relation}</span>}
      <span className={s.call} aria-hidden="true">
        {call && (
          <>
            {t.call} <span lang={lang} dir={dir}>{call}</span>
          </>
        )}
      </span>
    </button>
  );
});
